/**
 * „Auftrag an Vespera“ — das Objekt und seine Regeln. (18.10.2026, Paket P1)
 *
 * Ein Entwickler (Schmied, Zimmermann — NIE der Hüter) legt Martin einen Auftrag vor: Ziel, Dienst, erlaubte Domains, erlaubte
 * Werkzeuge (Positivliste), Schritte, erwartetes Ergebnis, Zeitlimit. Martin sieht eine Karte mit genau diesem Inhalt; sein Knopf
 * „Ja, ausführen“ setzt den Status auf `freigegeben`. In P1 wird NICHTS ausgeführt — kein Browser, kein Werkzeug.
 *
 * ⚠️ Der Hash (SHA-256 über den kanonischen Inhalt) bindet Martins Ja an GENAU diesen Inhalt. Die Werkzeugnamen sind in P1 reine Namen.
 * ⚠️ Rein: kein Dateizugriff, keine Uhr (die Zeit kommt als Argument). Wird von Server und Seite gleich gelesen.
 */
import { schwaerzeText } from "./geheim.js";

/** Name des SSE-Ereignisses auf dem Chat-Strom. */
export const AUFTRAEGE_EREIGNIS = "vespera-auftraege";

/** Wer Aufträge stellen darf — der Hüter nicht. */
export const AUFTRAGGEBER = ["schmied", "zimmermann"] as const;
export type Auftraggeber = (typeof AUFTRAGGEBER)[number];

export const STATUS = ["vorgeschlagen", "freigegeben", "abgelehnt", "abgelaufen"] as const;
export type AuftragStatus = (typeof STATUS)[number];

/**
 * Die Positivliste der Werkzeuge (Namen ohne Wirkung in P1). Bewusst NICHT darin: browser_form_submit (im Lauf gesperrt),
 * alles mit Zahlung, Mail, Shell oder Datei.
 */
export const ERLAUBTE_WERKZEUGE: readonly string[] = [
  "browser_navigate",
  "browser_snapshot",
  "browser_screenshot",
  "browser_read_text",
  "browser_click",
  "browser_type",
  "browser_select",
  "browser_wait",
];

/** Grenzen. */
export const ZEITLIMIT_MIN_HOECHSTENS = 30;
export const SCHRITTE_HOECHSTENS = 12;
export const SCHRITT_ZEICHEN = 300;
export const ZIEL_ZEICHEN = 300;
export const DIENST_ZEICHEN = 60;
export const ERWARTET_ZEICHEN = 500;
export const DOMAINS_HOECHSTENS = 10;
export const JE_STUNDE = 6;
export const JE_TAG = 20;
/** So lange wartet der Vorschlag auf Martins Ja; danach `abgelaufen`. */
export const JA_FRIST_MS = 10 * 60_000;
/** So jung darf ein Vorschlag für ein Ja nicht sein (ein Ja ohne Lesezeit ist kein Ja). */
export const JA_MINDESTALTER_MS = 2000;
/** So lange nach dem Ja ist der Start möglich; danach `abgelaufen`. */
export const START_FENSTER_MS = 10 * 60_000;
export const STUNDE_MS = 60 * 60_000;
export const TAG_MS = 24 * STUNDE_MS;
/** Wie viele Aufträge die Ablage behält (und wie lange). */
export const ABLAGE_HOECHSTENS = 200;
export const ABLAGE_VERFALL_MS = 7 * TAG_MS;

export interface AuftragInhalt {
  auftraggeber: Auftraggeber;
  ziel: string;
  dienst: string;
  domains: string[];
  werkzeuge: string[];
  schritte: string[];
  erwartet: string;
  zeitlimitMin: number;
}

export interface Auftrag extends AuftragInhalt {
  id: string;
  hash: string;
  status: AuftragStatus;
  erstelltMs: number;
  /** Wann der Status zuletzt wechselte. */
  geaendertMs: number;
  /** Nur bei `freigegeben`: bis wann der Start möglich ist (P3); danach `abgelaufen`. */
  freigegebenBis?: number;
  /** Kurzer fester Grund bei `abgelehnt`/`abgelaufen` (kein Wortlaut). */
  grund?: "nein" | "abgebrochen" | "frist";
}

export function istAuftraggeber(w: unknown): w is Auftraggeber {
  return typeof w === "string" && (AUFTRAGGEBER as readonly string[]).includes(w);
}

/** Ein Hostname: Kleinbuchstaben/Ziffern/Bindestrich, mindestens zwei Teile, keine IP, kein Schema/Port/Pfad/Stern. */
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
export function hostnameAus(roh: unknown): string | null {
  if (typeof roh !== "string") return null;
  const h = roh.trim().toLowerCase().replace(/\.$/, "");
  if (h.length < 4 || h.length > 253) return null;
  const teile = h.split(".");
  if (teile.length < 2 || !teile.every((t) => LABEL.test(t))) return null;
  if (teile.every((t) => /^\d+$/.test(t))) return null; // IP-Adresse
  if (!/[a-z]/.test(teile[teile.length - 1]!)) return null;
  if (h === "localhost" || h.endsWith(".localhost") || h.endsWith(".local") || h.endsWith(".internal")) return null;
  return h;
}

/**
 * Verbotene Zeichen in Textfeldern: Steuerzeichen (außer Tab, Zeilenumbruch), Unicode-Kategorien Cf (Formatzeichen: Null-Breite, Bidi-
 * Steuerung, weicher Bindestrich, BOM), Co (privat), Cn (unbelegt), dazu Tag-Zeichen U+E0000–E007F. Sie machen unsichtbaren oder
 * umgedrehten Text, den Martin auf der Karte nicht sähe.
 */
// eslint-disable-next-line no-control-regex
const VERBOTEN_ZEICHEN = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u{E0000}-\u{E007F}]|\p{Cf}|\p{Co}|\p{Cn}/u;

/** Ein Textfeld: Text, einzeilig gefaltet, ohne verbotene Zeichen, 1..max. */
function textAus(roh: unknown, max: number): string | null {
  if (typeof roh !== "string") return null;
  if (VERBOTEN_ZEICHEN.test(roh)) return null;
  const t = roh.replace(/\s+/g, " ").trim();
  return t.length >= 1 && t.length <= max ? t : null;
}

export type PruefFehler =
  | "auftraggeber"
  | "ziel"
  | "dienst"
  | "domains"
  | "werkzeuge"
  | "schritte"
  | "erwartet"
  | "zeitlimit"
  | "geheimnis";

/** Prüft und glättet einen Auftrag aus dem Rumpf. Rein. Das Geheimnis-Muster (shared/geheim.ts) lehnt ab, es schwärzt nicht. */
export function pruefeInhalt(roh: unknown, auftraggeber: unknown): { ok: true; inhalt: AuftragInhalt } | { ok: false; fehler: PruefFehler } {
  const b = roh && typeof roh === "object" && !Array.isArray(roh) ? (roh as Record<string, unknown>) : {};
  if (!istAuftraggeber(auftraggeber)) return { ok: false, fehler: "auftraggeber" };
  const ziel = textAus(b.ziel, ZIEL_ZEICHEN);
  if (!ziel) return { ok: false, fehler: "ziel" };
  const dienst = textAus(b.dienst, DIENST_ZEICHEN);
  if (!dienst) return { ok: false, fehler: "dienst" };
  const erwartet = textAus(b.erwartet, ERWARTET_ZEICHEN);
  if (!erwartet) return { ok: false, fehler: "erwartet" };

  if (!Array.isArray(b.domains) || b.domains.length < 1 || b.domains.length > DOMAINS_HOECHSTENS) return { ok: false, fehler: "domains" };
  const domains: string[] = [];
  for (const d of b.domains) {
    const h = hostnameAus(d);
    if (!h) return { ok: false, fehler: "domains" };
    if (!domains.includes(h)) domains.push(h);
  }

  if (!Array.isArray(b.werkzeuge) || b.werkzeuge.length < 1 || b.werkzeuge.length > ERLAUBTE_WERKZEUGE.length) return { ok: false, fehler: "werkzeuge" };
  const werkzeuge: string[] = [];
  for (const w of b.werkzeuge) {
    if (typeof w !== "string" || !ERLAUBTE_WERKZEUGE.includes(w)) return { ok: false, fehler: "werkzeuge" };
    if (!werkzeuge.includes(w)) werkzeuge.push(w);
  }

  if (!Array.isArray(b.schritte) || b.schritte.length < 1 || b.schritte.length > SCHRITTE_HOECHSTENS) return { ok: false, fehler: "schritte" };
  const schritte: string[] = [];
  for (const s of b.schritte) {
    const t = textAus(s, SCHRITT_ZEICHEN);
    if (!t) return { ok: false, fehler: "schritte" };
    schritte.push(t);
  }

  const z = b.zeitlimitMin;
  if (typeof z !== "number" || !Number.isInteger(z) || z < 1 || z > ZEITLIMIT_MIN_HOECHSTENS) return { ok: false, fehler: "zeitlimit" };

  const inhalt: AuftragInhalt = { auftraggeber, ziel, dienst, domains, werkzeuge, schritte, erwartet, zeitlimitMin: z };
  if (enthaeltGeheimnis(inhalt)) return { ok: false, fehler: "geheimnis" };
  return { ok: true, inhalt };
}

/** Schlüsselblöcke und Passwort-Hinweise: nie in einem Auftrag. */
const SCHLUESSEL_BLOCK = /-----\s*BEGIN[^-]{0,40}PRIVATE\s*KEY\s*-----/i;
const PASSWORT_HINWEIS = /passwort|kennwort|password\s*[:=]/i;

/**
 * Steht im Inhalt etwas, das wie ein Schlüssel oder ein Passwort aussieht? Jedes Textfeld einzeln, alles zusammen — und beides auch mit
 * entferntem Leerraum (ein Schlüssel, den man über Zeilen oder Schritte verteilt). Rein.
 */
export function enthaeltGeheimnis(i: AuftragInhalt): boolean {
  const einzeln = [i.ziel, i.dienst, i.erwartet, ...i.schritte, ...i.domains];
  const zusammen = [i.ziel, i.dienst, ...i.schritte, i.erwartet];
  const felder = [...einzeln, zusammen.join("\n"), zusammen.join(" "), zusammen.join("")];
  const ohneLeerraum = felder.map((f) => f.replace(/\s+/g, ""));
  return [...felder, ...ohneLeerraum].some((f) => schwaerzeText(f) !== f || SCHLUESSEL_BLOCK.test(f) || PASSWORT_HINWEIS.test(f));
}

/** Der kanonische Inhalt (feste Feldreihenfolge, sortierte Mengen) — der Server rechnet daraus den SHA-256 (server/vespera-auftraege.ts). Rein. */
export function kanonInhalt(i: AuftragInhalt): string {
  return JSON.stringify([i.auftraggeber, i.ziel, i.dienst, [...i.domains].sort(), [...i.werkzeuge].sort(), i.schritte, i.erwartet, i.zeitlimitMin]);
}

export interface AuftraegeStand {
  auftraege: Auftrag[];
}

/**
 * Der Strom-Stand für die Seite: streng in der Form, der Hash wurde vom Server gerechnet (die Seite schickt ihn mit dem Ja zurück,
 * der Server prüft ihn gegen den neu gerechneten). Rein.
 */
export function leseAuftraegeStand(roh: unknown): AuftraegeStand | null {
  const liste = roh && typeof roh === "object" ? (roh as { auftraege?: unknown }).auftraege : null;
  if (!Array.isArray(liste)) return null;
  const aus: Auftrag[] = [];
  for (const e of liste.slice(0, ABLAGE_HOECHSTENS)) {
    const a = leseAuftragForm(e);
    if (a) aus.push(a);
  }
  return { auftraege: aus };
}

/** Form-Prüfung eines Auftrags ohne Hash-Neuberechnung (Seite) — `hashPasst` liefert der Server. Rein. */
export function leseAuftragForm(roh: unknown): Auftrag | null {
  if (!roh || typeof roh !== "object") return null;
  const e = roh as Record<string, unknown>;
  const p = pruefeInhalt(e, e.auftraggeber);
  if (!p.ok) return null;
  if (typeof e.id !== "string" || !/^[0-9a-f]{8}$/.test(e.id)) return null;
  if (typeof e.status !== "string" || !(STATUS as readonly string[]).includes(e.status)) return null;
  const { erstelltMs, geaendertMs, hash } = e;
  if (typeof erstelltMs !== "number" || !Number.isFinite(erstelltMs) || typeof geaendertMs !== "number" || !Number.isFinite(geaendertMs)) return null;
  if (typeof hash !== "string" || !/^[0-9a-f]{64}$/.test(hash)) return null;
  const grund = e.grund === "nein" || e.grund === "abgebrochen" || e.grund === "frist" ? e.grund : undefined;
  const bis = typeof e.freigegebenBis === "number" && Number.isFinite(e.freigegebenBis) ? e.freigegebenBis : undefined;
  return { ...p.inhalt, id: e.id, hash, status: e.status as AuftragStatus, erstelltMs, geaendertMs, ...(grund ? { grund } : {}), ...(bis !== undefined ? { freigegebenBis: bis } : {}) };
}
